/**
 * MobileAddTeamManagersModal
 *
 * MUI full-screen Dialog replacement for AddTeamManagersModal (PrimeReact Dialog).
 * Mobile-first design with glassmorphic styling, sticky AppBar and bottom button.
 *
 * Components from MUI: Dialog, AppBar, Toolbar, IconButton, Typography, Autocomplete,
 *   TextField, Button, Box, Slide
 */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

// MUI Components
import {
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Autocomplete,
  TextField,
  Button,
  Box,
  Slide,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

// Redux
import { getCollabsNames } from "../../../components/vcomp/vcompSlice";
import {
  setTeamManagers,
  addTeamManager,
} from "../../../components/teams/teamsSlice";

// Shared utilities
import Langs from "../../../app/lang/langs";
import { showErrorToast } from "../../../app/utils/helpers";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_CARD = "16px";
const BORDER_RADIUS_INPUT = "12px";
const BORDER_RADIUS_BUTTON = "14px";

const fieldLabelSx = {
  fontWeight: 600,
  mb: 0.75,
  letterSpacing: "-0.01em",
  color: "text.secondary",
  fontSize: "0.82rem",
  textTransform: "uppercase",
};

const glassmorphicTextFieldSx = {
  mb: 3,
  "& .MuiOutlinedInput-root": {
    borderRadius: BORDER_RADIUS_INPUT,
    backgroundColor: "rgba(255,255,255,0.06)",
    backdropFilter: "blur(6px)",
    transition: TRANSITION_SMOOTH,
    "& fieldset": {
      borderColor: "rgba(0,0,0,0.08)",
      transition: TRANSITION_SMOOTH,
    },
    "&:hover fieldset": {
      borderColor: "rgba(0,0,0,0.18)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255,144,21,0.5)",
      boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
    },
    "&.Mui-focused": {
      backgroundColor: "rgba(255,255,255,0.10)",
    },
  },
  "& input": {
    fontSize: "16px",
    transition: TRANSITION_SMOOTH,
  },
  "& input::placeholder": {
    opacity: 0.45,
  },
};

/* ------------------------------------------------------------------ */
/*  Manager type options                                                */
/* ------------------------------------------------------------------ */
const MANAGER_TYPE_OPTIONS = [
  { label: "Manager", value: "MANAGER" },
  { label: "Responsable de compte", value: "RESPCOMPTE" },
  { label: "SDM", value: "SDM" },
];

/* ------------------------------------------------------------------ */
/*  Slide transition                                                    */
/* ------------------------------------------------------------------ */
const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */
export default function MobileAddTeamManagersModal(props) {
  const dispatch = useDispatch();

  // Redux state
  const collabsNames = useSelector((state) => state.vcomp.collabsNames);

  // Local state
  const [filteredCollabs, setFilteredCollabs] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);
  const [managerType, setManagerType] = useState(MANAGER_TYPE_OPTIONS[0]);

  /* ---- Effects ---- */

  useEffect(() => {
    dispatch(getCollabsNames());
  }, [dispatch]);

  useEffect(() => {
    setFilteredCollabs(
      collabsNames
        .filter(
          (collab) =>
            !props.prevManagers.some(
              (manager) => manager.id_collab === collab.id
            )
        )
        .map(({ id, nom, prenom, email }) => ({
          id,
          nom,
          prenom,
          email,
          fullName: `${prenom} ${nom}`,
        }))
    );
  }, [collabsNames, props.prevManagers]);

  /* ---- Handlers ---- */

  const handleClose = () => {
    props.setVisible(false);
    setSelectedManager(null);
    setManagerType(MANAGER_TYPE_OPTIONS[0]);
  };

  const handleAddManager = () => {
    if (selectedManager && props.idTeam) {
      const managerData = {
        id_team: props.idTeam,
        id_collab: selectedManager.id,
        type: managerType.value,
      };

      dispatch(addTeamManager(managerData))
        .then((actionResponse) => {
          if (addTeamManager.fulfilled.match(actionResponse)) {
            const newManager = {
              id: actionResponse.payload.id,
              id_team: props.idTeam,
              id_collab: selectedManager.id,
              type: managerType.value,
              nom: selectedManager.nom,
              prenom: selectedManager.prenom,
              email: selectedManager.email,
            };

            dispatch(
              setTeamManagers([...props.prevManagers, newManager])
            );
            handleClose();
          }
        })
        .catch((error) => {
          console.error("Erreur lors de l'ajout du manager :", error);
          showErrorToast("Erreur lors de l'ajout du manager.");
        });
    }
  };

  /* ---- Render ---- */

  return (
    <Dialog
      fullScreen
      open={!!props.visible}
      onClose={handleClose}
      TransitionComponent={SlideUp}
      sx={{ zIndex: 1400 }}
    >
      {/* ---- Sticky AppBar ---- */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: "rgba(255,255,255,0.82)",
          color: "text.primary",
          boxShadow: "0 1px 12px rgba(0,0,0,0.06)",
          backdropFilter: "blur(14px)",
        }}
      >
        <Toolbar sx={{ minHeight: 56 }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
            sx={{ mr: 1 }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            variant="h6"
            sx={{ flex: 1, fontWeight: 700, fontSize: "1.1rem" }}
          >
            <Langs str="ADD_MANAGER" />
          </Typography>
        </Toolbar>
      </AppBar>

      {/* ---- Body ---- */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 2,
          pt: 3,
          pb: "120px",
          bgcolor: "#f5f5f7",
        }}
      >
        <Box
          sx={{
            borderRadius: BORDER_RADIUS_CARD,
            bgcolor: "rgba(255,255,255,0.72)",
            backdropFilter: "blur(18px)",
            boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
            p: 2.5,
          }}
        >
          {/* ---- Manager Selection ---- */}
          <Typography sx={fieldLabelSx}>
            <Langs str="Select Manager" />
          </Typography>
          <Autocomplete
            value={selectedManager}
            onChange={(_, newValue) => setSelectedManager(newValue)}
            options={filteredCollabs}
            getOptionLabel={(option) => option.fullName || ""}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Sélectionner un manager..."
                sx={glassmorphicTextFieldSx}
              />
            )}
            componentsProps={{
              popper: { sx: { zIndex: 1500 } },
            }}
            noOptionsText="Aucun résultat"
          />

          {/* ---- Manager Type Selection ---- */}
          <Typography sx={fieldLabelSx}>
            <Langs str="MANAGER_TYPE" />
          </Typography>
          <Autocomplete
            value={managerType}
            onChange={(_, newValue) => {
              if (newValue) setManagerType(newValue);
            }}
            options={MANAGER_TYPE_OPTIONS}
            getOptionLabel={(option) => option.label || ""}
            isOptionEqualToValue={(option, value) =>
              option.value === value?.value
            }
            disableClearable
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Type de manager"
                sx={glassmorphicTextFieldSx}
              />
            )}
            componentsProps={{
              popper: { sx: { zIndex: 1500 } },
            }}
            noOptionsText="Aucun résultat"
          />
        </Box>
      </Box>

      {/* ---- Sticky Bottom Button ---- */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          px: 2,
          py: 2,
          bgcolor: "rgba(255,255,255,0.88)",
          backdropFilter: "blur(14px)",
          borderTop: "1px solid rgba(0,0,0,0.06)",
          boxShadow: "0 -2px 12px rgba(0,0,0,0.04)",
        }}
      >
        <Button
          fullWidth
          variant="contained"
          disabled={!selectedManager}
          onClick={handleAddManager}
          startIcon={<PersonAddIcon />}
          sx={{
            borderRadius: BORDER_RADIUS_BUTTON,
            minHeight: 52,
            fontWeight: 700,
            fontSize: "1rem",
            textTransform: "none",
            bgcolor: "#FF9015",
            color: "#fff",
            boxShadow: "0 4px 14px rgba(255,144,21,0.35)",
            "&:hover": {
              bgcolor: "#e8820f",
              boxShadow: "0 6px 20px rgba(255,144,21,0.45)",
            },
            "&:active": {
              transform: "scale(0.97)",
            },
            "&.Mui-disabled": {
              bgcolor: "rgba(255,144,21,0.35)",
              color: "rgba(255,255,255,0.7)",
            },
            transition: TRANSITION_SMOOTH,
          }}
        >
          <Langs str="ADD" />
        </Button>
      </Box>
    </Dialog>
  );
}
